import PQueue from 'p-queue';

interface Tokens {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
}

class AuthService {
  private tokens: Tokens | null = null;
  private queue: PQueue;

  constructor() {
    this.queue = new PQueue({ concurrency: 1 });
  }

  private async initialize(): Promise<void> {
    console.log("AuthService: Initializing");
    try {
      const ktorBackendUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8080';
      const url = `${ktorBackendUrl}/ktor/register-service`
      console.log("AuthService: Fetching tokens from", url);
      const response = await fetch(`${ktorBackendUrl}/ktor/register-service`, {
        method: 'POST',
        headers: {
          'X-API-Key': process.env.SERVICE_API_KEY!,
          'X-API-ID': process.env.SERVICE_ID!,
        },
      });

      if (!response.ok) {
        throw new Error(`Service registration failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log("AuthService: Tokens received from backend");

      const expiresAt = Date.now() + 3600000; // Assume 1 hour expiration, adjust as needed

      this.tokens = {
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        expiresAt,
      };
      console.log("AuthService: Tokens stored");
    } catch (error) {
      console.error("AuthService: Error during initialization:", error);
      throw error;
    }
  }

  async getAccessToken(): Promise<string> {
    const token = await this.queue.add(async (): Promise<string> => {
      if (!this.tokens || Date.now() > this.tokens.expiresAt) {
        await this.initialize();
      }
      if (!this.tokens) {
        throw new Error('Failed to initialize tokens');
      }
      return this.tokens.accessToken;
    });

    if (typeof token !== 'string') {
      throw new Error('Failed to retrieve access token');
    }

    return token;
  }

  async refreshTokens(): Promise<void> {
    return this.queue.add(async () => {
      if (!this.tokens || !this.tokens.refreshToken) {
        console.log("No refresh token available, re-initializing...");
        await this.initialize();
        return;
      }

      try {
        console.warn("TOKEN REFRESH TRIGGERED");
        const ktorBackendUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8080';
        const response = await fetch(`${ktorBackendUrl}/ktor/refresh-token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': process.env.SERVICE_API_KEY!,
            'X-API-ID': process.env.SERVICE_ID!,
          },
          body: JSON.stringify({ token: this.tokens.refreshToken }),
        });

        const data = await response.json();

        if (!response.ok) {
          if (response.status === 401) {
            console.log("Refresh token expired or invalid, re-initializing...");
            this.tokens = null;
            await this.initialize();
            return;
          }
          throw new Error(data.error || 'Failed to refresh tokens');
        }

        const expiresAt = Date.now() + 3600000; // Assume 1 hour expiration, adjust as needed

        this.tokens = {
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          expiresAt,
        };

        console.warn("TOKENS REFRESHED: ", JSON.stringify(this.tokens.refreshToken));
      } catch (error) {
        console.error("Error refreshing tokens:", error);
        this.tokens = null;
        throw error;
      }
    });
  }

  async forceReInitialize(): Promise<void> {
    return this.queue.add(async () => {
      this.tokens = null;
      await this.initialize();
    });
  }
}

export const authService = new AuthService();